import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import { getSubdomain, parse } from 'tldts';
import {
    getPartialBillingData,
    getCachedBillingData,
    updateCustomDomain,
    checkCustomDomain,
    checkDomainDns,
    validateDomain,
} from '../../data/api';
import * as Sentry from '@sentry/react';

import SpinnerButton from '../shared/SpinnerButton';
import DNSTable from './DNSTable';
import DomainStatusField from './DomainStatusField';
import DomainTestResults from './DomainTestResults';
import Loading from '../shared/Loading';
import { buildDomainTestResults } from '../../utils/domain-test-results';
import {
    DEFAULT_PRO_DOMAIN,
    GHOST_REDIRECT_SERVER_IP,
    ROUTES,
    CUSTOM_DOMAIN_SSL_STATES,
    CUSTOM_DOMAIN_FINAL_STATUSES,
    CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES,
    CUSTOM_DOMAIN_SSL_FAILED_STATUSES,
    CUSTOM_DOMAIN_SSL_PENDING_STATUSES,
} from '../../utils/constants';
import getForceUpgradeStatus, { FORCE_UPGRADE_STATUS } from '../../utils/get-force-upgrade-status';

const NO_APEX_DOMAIN_NAMESERVERS = ['godaddy', 'hover', 'google'];

// The DNS records table has three states:
// preview - no domain entered yet, show a wireframe of the records
// loading - a domain is entered but the records haven't been determined
//           yet, show a spinner on top of the wireframe
// ready   - the records are determined and shown
const DNS_RECORDS_STATES = {
    PREVIEW: 'preview',
    LOADING: 'loading',
    READY: 'ready',
};

// The records test has three states:
// idle    - no test run for the current domain yet
// testing - waiting for the dry-run validation response
// done    - results are shown; the button becomes "Test again"
const DNS_TEST_STATES = {
    IDLE: 'idle',
    TESTING: 'testing',
    DONE: 'done',
};

// A valid custom domain parses to a registrable domain with a known ICANN
// suffix. tldts allows real-world DNS labels the hostname RFCs don't (e.g.
// underscores in subdomains) while rejecting invalid characters and made-up
// TLDs. The hostname comparison rejects inputs that aren't purely a hostname,
// like URLs or values with a trailing dot.
const isValidDomainName = (domainValue) => {
    const { domain, hostname, isIcann } = parse(domainValue);
    return Boolean(domain && isIcann && hostname === domainValue);
};

// How long to wait after the user stops typing before looking up the DNS
// records, so the table updates without requiring a blur
const DNS_CHECK_DEBOUNCE_MS = 500;

// We should have four states of a submit button
// remove when input value is empty -> shouldn't be allowed (black button disabled)
// 1. update when new domain is different to old domain (black button)
// 2. activate when new domain is entered and valid and no old domain exists (black button)
// 3. try again when domain setup failed for whatever reason
// 4. continue (which also fetches fresh subscription data) after a successful activation (green button)
const BUTTON_STATES = {
    fail: {
        className: 'gh-btn gh-btn-block',
        html: 'Try again',
        onSubmit: 'handleSubmit',
        onBack: 'reloadAndBack',
        disabled: false,
    },
    pending: {
        className: 'gh-btn gh-btn-block spinner',
        html: '<span><div class="gh-spinner"></div>Activation may take up to 10 minutes</span>',
        onBack: 'reloadAndBack',
        disabled: true,
    },
    default: {
        className: 'gh-btn gh-btn-block',
        html: 'Activate',
        onSubmit: 'handleSubmit',
        disabled: true,
    },
};

/**
 * @typedef {import('../../types').BillingData} BillingData
 * @typedef {object} CustomDomainProps
 * @property {(title: string) => void} updateTitle
 * @property {{push: (route: string) => void}} history
 * @property {(visible: boolean) => void} showNav
 * @property {{state?: object}} location
 */

/**
 * Manages the publication's custom domain.
 * @param {CustomDomainProps} props
 * @returns {React.ReactNode}
 */
function CustomDomain({ updateTitle, history, showNav, location }) {
    const isMountedRef = useRef(false);

    const [loading, setLoading] = useState(true);
    const [disableInput, setDisableInput] = useState(false);
    const [newDomain, setNewDomain] = useState('');
    const [buttonState, setButtonState] = useState(BUTTON_STATES.default);
    const [timeoutId, setTimeOutId] = useState(null);
    const [error, setError] = useState(null);
    const [currentSite, setCurrentSite] = useState(null);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [proDomain, setProDomain] = useState(null);
    const [cname, setCname] = useState('www');
    const [showRemoveDomain, setShowRemoveDomain] = useState(false);
    const [showDomainStatusField, setShowDomainStatusField] = useState(false);
    const [backBtnDisabled, setBackBtnDisabled] = useState(false);
    const [nameserver, setNameserver] = useState('unknown');
    const [dnsRecordsState, setDnsRecordsState] = useState(DNS_RECORDS_STATES.PREVIEW);
    const [dnsTestState, setDnsTestState] = useState(DNS_TEST_STATES.IDLE);
    const [dnsTestResults, setDnsTestResults] = useState(null);
    const [dnsTestError, setDnsTestError] = useState(null);
    const dnsDebounceRef = useRef(null);
    const lastDnsCheckDomainRef = useRef(null);

    useEffect(() => {
        if (showNav && loading) {
            showNav(false);
        }
    }, [showNav, loading]);

    const reloadAndBack = async (event) => {
        event.preventDefault();

        if (timeoutId) {
            // Ensure to stop the polling
            clearTimeout(timeoutId);
        }

        setButtonState(BUTTON_STATES.default);
        setDisableInput(true);
        setLoading(true);
        setNewDomain('');

        showNav(false);

        const updatedSite = await getPartialBillingData('currentSite');

        if (!isMountedRef.current) {
            return;
        }

        setCurrentSite(updatedSite);
        setLoading(false);

        showNav(true);

        return history.push(ROUTES.DOMAIN);
    };

    const handleSubmit = async (event) => {
        event?.preventDefault();

        setDisableInput(true);
        setButtonState(BUTTON_STATES.pending);
        setError(null);
        setBackBtnDisabled(true);

        if (
            (!newDomain || newDomain === currentSite?.external_domain) &&
            !CUSTOM_DOMAIN_SSL_FAILED_STATUSES.includes(currentSite?.ssl_status)
        ) {
            setError('Invalid custom domain');

            Sentry.withScope((scope) => {
                scope.setTags({
                    source: 'handleSubmit',
                    shown_to_user: true,
                });
                scope.setExtra('siteId', currentSite?.id);
                Sentry.captureMessage('Invalid custom domain', 'warning');
            });

            setShowDomainStatusField(false);
            setButtonState(BUTTON_STATES.fail);
            setDisableInput(false);
            setBackBtnDisabled(false);
            return;
        }

        let finalDomain = newDomain;

        // Ensure nameserver is up-to-date before checking
        const nameserverRes = await checkDomainDns({ domain: newDomain, siteId: currentSite?.id });
        if (
            nameserverRes.nameServer &&
            NO_APEX_DOMAIN_NAMESERVERS.includes(nameserverRes.nameServer) &&
            getSubdomain(newDomain) === ''
        ) {
            finalDomain = `www.${newDomain}`;
        }

        try {
            // When updating or activating a custom domain, we use a
            // background function which always returns a 202.
            // The error handling has to happen when we poll the site.
            await updateCustomDomain({
                siteId: currentSite?.id,
                domain: finalDomain,
            });

            setTimeOutId(
                setTimeout(() => {
                    pollSite({ currentSite });
                }, 3000),
            );
            return;
        } catch (err) {
            Sentry.captureException(err, {
                tags: {
                    source: 'handleSubmit',
                    shown_to_user: true,
                },
                extra: {
                    siteId: currentSite?.id,
                },
                user: {
                    id: currentSite?.user_id,
                },
            });
        }
    };

    const handleRemoveDomain = async (event) => {
        event.preventDefault();

        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setError(null);
        setLoading(true);

        const res = await updateCustomDomain({
            siteId: currentSite?.id,
            domain: null,
        });

        if (!isMountedRef.current) {
            return;
        }

        if (res?.error) {
            const message =
                res?.error?.message ??
                'Unable to remove domain. Refresh the page and try again or contact support@ghost.org.';
            setError(message);

            Sentry.withScope((scope) => {
                scope.setTags({
                    source: 'handleRemoveDomain',
                    shown_to_user: true,
                });
                scope.setContext('full_error', res?.error);
                scope.setExtra('siteId', currentSite?.id);
                Sentry.captureMessage(message, 'warning');
            });

            setLoading(false);

            return;
        }

        return reloadAndBack(event);
    };

    const pollSite = async ({ currentSite: site }) => {
        setCurrentSite(site);

        const res = await checkCustomDomain({
            siteId: site?.id,
        });

        if (!isMountedRef.current) {
            return;
        }

        setBackBtnDisabled(false);

        if (res?.error) {
            const message =
                res.error?.message ||
                "We weren't able to activate your custom domain. Contact support@ghost.org";

            Sentry.withScope((scope) => {
                scope.setTags({
                    source: 'pollSite',
                    shown_to_user: true,
                });
                scope.setContext('full_error', res?.error);
                scope.setExtra('siteId', currentSite?.id);
                Sentry.captureMessage(message, 'warning');
            });

            setDisableInput(false);
            setError(message);
            setShowDomainStatusField(false);
            setButtonState(BUTTON_STATES.fail);
            return;
        }

        const isDomainUpdate =
            currentSite?.external_domain &&
            CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(res?.ssl_status) &&
            res?.hostname !== newDomain;
        const isDomainActivation =
            !currentSite?.external_domain &&
            !res?.hostname &&
            newDomain &&
            !currentSite?.ssl_status;

        // if the site has a custom domain and the new domain doesn't match
        // the now returned domain, the update process failed most likely due
        // to validation error, when the domain is already in use.
        if (isDomainActivation || isDomainUpdate) {
            const message = isDomainUpdate
                ? "We weren't able to update your custom domain. Refresh the page and try again or contact support@ghost.org"
                : "We weren't able to activate your custom domain. Refresh the page and try again or contact support@ghost.org";
            setDisableInput(false);
            setError(message);
            setShowDomainStatusField(false);
            setButtonState(BUTTON_STATES.fail);
            return;
        }

        // Allow the domain to be removed, once it's setup in the DB
        if (res?.ssl_status && res?.hostname) {
            if (res.hostname) {
                setCurrentSite({
                    ...site,
                    external_domain: res.hostname,
                    ssl_status: res.ssl_status,
                });
            }
        }

        if (res?.ssl_status && CUSTOM_DOMAIN_FINAL_STATUSES.includes(res.ssl_status)) {
            setDisableInput(false);

            setShowRemoveDomain(
                CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(res.ssl_status) ? true : false,
            );
            setShowDomainStatusField(
                CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(res.ssl_status) ? true : false,
            );

            if (CUSTOM_DOMAIN_SSL_FAILED_STATUSES.includes(res.ssl_status)) {
                let message =
                    "We weren't able to activate your custom domain. Contact support@ghost.org.";

                if (res.ssl_status === CUSTOM_DOMAIN_SSL_STATES.INVALID) {
                    if (!cname) {
                        message = `Unable to detect a valid CNAME record for ${newDomain}. Did you mean to enter www.${newDomain}?`;
                    } else {
                        message = `Unable to detect a valid CNAME record for ${newDomain}.`;
                    }
                }
                setError(message);
                setButtonState(BUTTON_STATES.fail);

                Sentry.withScope((scope) => {
                    scope.setTags({
                        source: 'pollSite',
                        shown_to_user: true,
                    });
                    scope.setExtra('newDomain', newDomain);
                    scope.setExtra('siteId', currentSite?.id);
                    Sentry.captureMessage('Unable to detect a valid CNAME record', 'info');
                });

                return;
            }
        } else if (
            res?.ssl_status &&
            CUSTOM_DOMAIN_SSL_PENDING_STATUSES.includes(res?.ssl_status)
        ) {
            // We're still going
            setTimeOutId(
                setTimeout(() => {
                    pollSite({ currentSite: site });
                }, 3000),
            );
            return;
        } else {
            setError('Refresh the page to update the status of your domain activation.');

            Sentry.withScope((scope) => {
                scope.setTags({
                    source: 'pollSite',
                    shown_to_user: true,
                });
                scope.setExtra('siteId', currentSite?.id);
                Sentry.captureMessage(
                    'Refresh the page to update the status of your domain activation.',
                    'warning',
                );
            });

            setShowDomainStatusField(false);
            setButtonState(BUTTON_STATES.fail);
            return;
        }
    };

    const runDnsCheck = (domainValue) => {
        if (lastDnsCheckDomainRef.current === domainValue) {
            // The records for this domain are already being looked up or shown
            return;
        }

        lastDnsCheckDomainRef.current = domainValue;
        setDnsRecordsState(DNS_RECORDS_STATES.LOADING);

        checkDomainDns({
            domain: domainValue,
            siteId: currentSite?.id,
        }).then((res) => {
            if (!isMountedRef.current || lastDnsCheckDomainRef.current !== domainValue) {
                // The user has since moved on to a different domain
                return;
            }

            if (res.nameServer) {
                setNameserver(res.nameServer);
            } else {
                setNameserver('unknown');
            }

            setCNAMEValue({ domain: domainValue, currentNameserver: res.nameServer });
            setDnsRecordsState(DNS_RECORDS_STATES.READY);
        });
    };

    const resetDnsTest = () => {
        setDnsTestState(DNS_TEST_STATES.IDLE);
        setDnsTestResults(null);
        setDnsTestError(null);
    };

    // Dry-run Daisy's activation checks (DNS resolution, Ghost(Pro) targeting,
    // proxy and CAA checks) so problems surface before hitting Activate.
    const handleTestRecords = async (event) => {
        event.preventDefault();

        setDnsTestState(DNS_TEST_STATES.TESTING);
        setDnsTestError(null);

        const res = await validateDomain({
            siteId: currentSite?.id,
            domain: newDomain,
        });

        if (!isMountedRef.current) {
            return;
        }

        const results = buildDomainTestResults(res);

        if (res?.error || !results) {
            setDnsTestState(dnsTestResults ? DNS_TEST_STATES.DONE : DNS_TEST_STATES.IDLE);
            setDnsTestError("We couldn't test your records. Try again in a few minutes.");

            Sentry.withScope((scope) => {
                scope.setTags({
                    source: 'handleTestRecords',
                    shown_to_user: true,
                });
                scope.setContext('full_error', res?.error);
                scope.setExtra('siteId', currentSite?.id);
                Sentry.captureMessage('Unable to test DNS records', 'warning');
            });

            return;
        }

        setDnsTestResults(results);
        setDnsTestState(DNS_TEST_STATES.DONE);
    };

    const isCheckableDomain = (domainValue) => {
        return Boolean(
            domainValue && isValidDomainName(domainValue) && !domainValue.endsWith('.' + proDomain),
        );
    };

    const handleDomainValidationOnBlur = (event) => {
        event.preventDefault();

        if (dnsDebounceRef.current) {
            clearTimeout(dnsDebounceRef.current);
        }

        let isValid = true;

        let newDomainValue = event.target?.value && event.target.value.trim().toLowerCase();
        // Try to parse as URL and extract hostname if it's a valid URL
        // Use URL.canParse if available (modern browsers), fallback to try-catch for test environment
        if (typeof URL.canParse === 'function' && URL.canParse(newDomainValue)) {
            newDomainValue = new URL(newDomainValue).hostname;
            setNewDomain(newDomainValue);
        } else {
            try {
                const url = new URL(newDomainValue);
                newDomainValue = url.hostname;
                setNewDomain(newDomainValue);
            } catch (e) {
                // Not a valid URL, use as-is
            }
        }

        const currentDomainValue =
            currentSite?.external_domain && currentSite?.external_domain.trim().toLowerCase();
        let errorValue = null;
        let buttonStateValue = { ...BUTTON_STATES.default, disabled: false };

        if (!newDomainValue) {
            buttonStateValue.disabled = true;
            isValid = false;
        }

        if (currentDomainValue && newDomainValue) {
            if (currentDomainValue === newDomainValue) {
                setShowDomainStatusField(true);
                isValid = false;
            } else if (currentDomainValue !== newDomainValue) {
                setShowDomainStatusField(false);
            }
        }

        if (newDomainValue && !isValidDomainName(newDomainValue)) {
            buttonStateValue.disabled = true;
            errorValue = 'Enter a valid domain';
            isValid = false;
        }

        if (newDomainValue && newDomainValue.endsWith('.' + proDomain)) {
            buttonStateValue.disabled = true;
            errorValue = 'External domains should not end in ' + proDomain;
            isValid = false;
        }

        setCNAMEValue({ domain: newDomainValue });
        setNewDomain(newDomainValue);

        if (isValid) {
            runDnsCheck(newDomainValue);
        } else if (!newDomainValue) {
            lastDnsCheckDomainRef.current = null;
            setDnsRecordsState(DNS_RECORDS_STATES.PREVIEW);
        } else if (currentDomainValue === newDomainValue) {
            // The active custom domain was re-entered, its records are already known
            lastDnsCheckDomainRef.current = newDomainValue;
            setDnsRecordsState(DNS_RECORDS_STATES.READY);
        }
        // An invalid domain only shows the error message and leaves the
        // table in its current state

        setError(errorValue);
        setButtonState(buttonStateValue);

        return isValid;
    };

    const setCNAMEValue = ({ domain, currentNameserver = nameserver }) => {
        if (!domain) {
            setCname('www');
            return;
        }

        const subdomain = getSubdomain(domain);

        if (subdomain && subdomain.length >= 1) {
            // we have a subdomain
            setCname(subdomain);
        } else if (NO_APEX_DOMAIN_NAMESERVERS.includes(currentNameserver)) {
            // nameserver does not support root domain CNAME
            setCname('www');
        } else {
            // Root domain setup
            setCname('@');
        }
    };

    const handleDomainInputChange = (event) => {
        event.preventDefault();

        const newDomainValue = event.target?.value && event.target.value.trim().toLowerCase();
        const buttonStateValue = { ...buttonState, disabled: false };

        if (newDomainValue !== newDomain) {
            // Results describe the previously tested domain — drop them
            resetDnsTest();
        }

        if (!newDomainValue || newDomainValue?.length < 3) {
            buttonStateValue.disabled = true;
            setError(null);
        }

        // Look up the DNS records once the user stops typing, so the table
        // updates without requiring a blur. Errors stay blur-only.
        if (dnsDebounceRef.current) {
            clearTimeout(dnsDebounceRef.current);
        }

        if (isCheckableDomain(newDomainValue)) {
            dnsDebounceRef.current = setTimeout(() => {
                runDnsCheck(newDomainValue);
            }, DNS_CHECK_DEBOUNCE_MS);
        }

        setNewDomain(newDomainValue);
        setButtonState(buttonStateValue);
    };

    const contactSupport = (event) => {
        event.preventDefault();
        return (window.location.href = 'mailto:support@ghost.org');
    };

    const setSpinnerButtonAction = (event) => {
        if (buttonState?.disabled || !buttonState?.onSubmit) {
            return null;
        } else if (buttonState?.onSubmit === 'reloadAndBack') {
            return reloadAndBack(event);
        } else if (buttonState?.onSubmit === 'handleSubmit') {
            return handleSubmit(event);
        }
    };

    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;

            const fetchCachedBillingData = async () => {
                /** @type {BillingData} */
                const billingData = await getCachedBillingData();

                if (!isMountedRef.current) {
                    return;
                }

                const { currentSite: site, isGrace } = billingData;

                setProDomain(site?.pro_domain || DEFAULT_PRO_DOMAIN);

                Sentry.setTag('source', 'custom-domain');

                Sentry.setExtra('siteId', site?.id);
                Sentry.setExtra('site', site?.url);

                if (!billingData || billingData?.error) {
                    if (billingData?.error?.status === 403) {
                        setShowError(true);
                        setErrorMessage(
                            <>
                                You’re on a special legacy plan, if you need to make any billing
                                changes reach out to us on{' '}
                                <a href="mailto:support@ghost.org" onClick={contactSupport}>
                                    support@ghost.org
                                </a>
                            </>,
                        );
                    } else {
                        return history.push(ROUTES.DASHBOARD);
                    }
                }

                const forceUpgradeStatus = getForceUpgradeStatus(billingData);
                const isDunning = forceUpgradeStatus === FORCE_UPGRADE_STATUS.NONE && isGrace;

                if (isDunning) {
                    showNav(true);
                    // Return to Dashboard when user is in dunning to update card details
                    return history.push(ROUTES.DASHBOARD);
                }

                // An active subscription remains eligible for domain management while
                // the Force upgrade value in Ghost catches up.
                if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.LAPSED_TRIAL_OWNER) {
                    showNav(true);
                    return history.push(ROUTES.PLANS);
                }

                setCurrentSite(site);

                setShowRemoveDomain(
                    site?.external_domain &&
                        CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(site?.ssl_status)
                        ? true
                        : false,
                );

                setNewDomain(site?.external_domain ?? '');

                if (site?.external_domain) {
                    setCNAMEValue({ domain: site?.external_domain });
                    lastDnsCheckDomainRef.current = site.external_domain.trim().toLowerCase();
                    setDnsRecordsState(DNS_RECORDS_STATES.READY);
                }

                setLoading(false);

                if (CUSTOM_DOMAIN_SSL_PENDING_STATUSES.includes(site?.ssl_status)) {
                    // If we're still in a pending state, continue with polling
                    setButtonState(BUTTON_STATES.pending);
                    setDisableInput(true);
                    setShowDomainStatusField(false);
                    setTimeOutId(
                        setTimeout(() => {
                            pollSite({ currentSite: site });
                        }, 3000),
                    );
                } else if (CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(site?.ssl_status)) {
                    setShowDomainStatusField(true);
                } else if (CUSTOM_DOMAIN_SSL_FAILED_STATUSES.includes(site?.ssl_status)) {
                    setButtonState(BUTTON_STATES.fail);
                    setDisableInput(false);
                    setShowDomainStatusField(false);
                } else {
                    setButtonState(BUTTON_STATES.default);
                    setShowDomainStatusField(false);
                    setDisableInput(false);
                }

                showNav(false);
            };

            updateTitle('Domain settings');
            fetchCachedBillingData();
        }

        return () => {
            isMountedRef.current = false;
            if (timeoutId) {
                // Ensure to stop the polling
                clearTimeout(timeoutId);
            }
            if (dnsDebounceRef.current) {
                clearTimeout(dnsDebounceRef.current);
            }
        };
    }, []);

    const dnsRecords = [
        {
            record_type: 'CNAME',
            name: cname,
            value: `${currentSite?.subdomain}.${proDomain}`,
        },
        {
            record_type: 'A',
            name: `${cname === '@' ? 'www' : '@'}`,
            value: GHOST_REDIRECT_SERVER_IP,
        },
    ];

    if (cname && cname !== 'www' && cname !== '@') {
        // if we have a subdomain other than `www` we need to omit the last dns record
        // as there shouldn't be an A record setup for the root <-> www redirect.
        dnsRecords.pop();
    }

    return (
        <main className="main">
            <div className="domain" data-test-id="domain">
                {loading ? (
                    <Loading />
                ) : showError ? (
                    <p style={{ textAlign: 'center', gridColumn: '1 / 3' }}>{errorMessage}</p>
                ) : (
                    <div className="domaingrid">
                        <header className="domain-nav">
                            {buttonState?.onBack ? (
                                backBtnDisabled ? (
                                    // We need to wait until the first polling response, otherwise the external domain
                                    // is not set yet and we won't see it in the domain overview.
                                    <p className="link-back disabled">&larr; Back</p>
                                ) : (
                                    <Link
                                        to="/domain"
                                        className="link-back"
                                        onClick={reloadAndBack}
                                    >
                                        &larr; Back
                                    </Link>
                                )
                            ) : (
                                <Link to="/domain" className="link-back">
                                    &larr; Back
                                </Link>
                            )}
                            <div className="actions">
                                <a
                                    className="gh-btn gh-btn-green gh-btn-sm"
                                    href="https://ghost.org/help/using-custom-domains/?ref=billing.ghost.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-test-btn="custom-domain-help"
                                >
                                    Get help
                                </a>
                            </div>
                        </header>

                        <div className="box-label">1. Enter the domain you want to use</div>
                        <section id="step-1" className="domain-form">
                            <div className="">
                                <div>
                                    <label htmlFor="domain">Domain</label>
                                    <input
                                        id="domain"
                                        className="gh-input"
                                        type="domain"
                                        required
                                        name="domain"
                                        placeholder="www.example.com"
                                        disabled={disableInput}
                                        autoCorrect="off"
                                        tabIndex="1"
                                        autoFocus={
                                            CUSTOM_DOMAIN_FINAL_STATUSES.includes(
                                                currentSite?.ssl_status,
                                            )
                                                ? false
                                                : true
                                        }
                                        value={newDomain}
                                        onChange={(event) => handleDomainInputChange(event)}
                                        onBlur={(event) => handleDomainValidationOnBlur(event)}
                                        onKeyDown={(e) => {
                                            if (e.code === 'Enter') {
                                                if (handleDomainValidationOnBlur(e)) {
                                                    return handleSubmit(e);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </section>

                        <div className="box-label">2. Create your DNS records</div>
                        <DNSTable
                            dnsRecords={dnsRecords}
                            showStatus={false}
                            showCloudflare={nameserver === 'cloudflare'}
                            boxClass="domain-form"
                            nameLabel="Host"
                            preview={dnsRecordsState === DNS_RECORDS_STATES.PREVIEW}
                            loading={dnsRecordsState === DNS_RECORDS_STATES.LOADING}
                        >
                            {dnsRecordsState === DNS_RECORDS_STATES.READY && newDomain && (
                                <>
                                    {dnsTestState === DNS_TEST_STATES.DONE && (
                                        <DomainTestResults results={dnsTestResults} />
                                    )}
                                    <div className="domain-test-actions">
                                        <button
                                            className="gh-btn gh-btn-outline domain-test-btn"
                                            data-test-btn="test-dns-records"
                                            disabled={
                                                dnsTestState === DNS_TEST_STATES.TESTING ||
                                                disableInput
                                            }
                                            onClick={handleTestRecords}
                                        >
                                            {dnsTestState === DNS_TEST_STATES.TESTING ? (
                                                <span>
                                                    <div className="gh-spinner"></div>
                                                    Checking records…
                                                </span>
                                            ) : dnsTestState === DNS_TEST_STATES.DONE ? (
                                                'Test again'
                                            ) : (
                                                'Test records'
                                            )}
                                        </button>
                                        {dnsTestState === DNS_TEST_STATES.DONE &&
                                            dnsTestResults?.allPassed && (
                                                <span className="domain-test-hint pass">
                                                    Everything looks good — you&rsquo;re ready to
                                                    activate
                                                </span>
                                            )}
                                        {dnsTestError && (
                                            <span className="domain-test-hint fail">
                                                {dnsTestError}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                        </DNSTable>

                        <div className="box-label">3. Activate custom domain</div>
                        <section id="step-3" className="domain-form step-activate">
                            {!showDomainStatusField ? (
                                <SpinnerButton
                                    onClick={(event) => setSpinnerButtonAction(event)}
                                    className={buttonState.className}
                                    html={buttonState.html}
                                    disabled={buttonState.disabled}
                                    data-test-btn="activate-domain"
                                />
                            ) : (
                                <DomainStatusField
                                    status={currentSite?.ssl_status}
                                    domain={currentSite?.external_domain}
                                    type="custom-domain"
                                />
                            )}

                            {error && (
                                <div className="form-error">
                                    <p className="error-msg">{error}</p>
                                </div>
                            )}

                            {showRemoveDomain && (
                                <button
                                    className="domain-remove"
                                    data-test-btn="remove-domain"
                                    onClick={handleRemoveDomain}
                                >
                                    Remove custom domain
                                </button>
                            )}
                        </section>
                    </div>
                )}
            </div>
        </main>
    );
}

CustomDomain.propTypes = {
    updateTitle: PropTypes.func,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
    showNav: PropTypes.func,
    location: PropTypes.shape({
        state: PropTypes.object,
    }),
};

export default withRouter(CustomDomain);
